/**
 * The whole application's copy, English only, flat, dotted keys (§B7.6).
 *
 * §D0 gives `src/i18n/*` to task 06a alone, so this file carries the keys for screens tasks 06b, 07, 08 and 09
 * will write. `tests/i18n.test.ts` enforces "every key is referenced" **per namespace, gated on the screen
 * directory existing**, so an unreferenced `review.*` key is not a failure until `src/screens/review/` lands — and
 * then it is. A later task that needs a key which is not here is a change to *this* file: raise it, do not inline
 * a literal string.
 */
export const en = {
  // ---------------------------------------------------------------- always enforced
  "common.app_name": "LuciMigration",
  "common.boot": "Loading the application shell.",
  "common.loading": "Loading",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.none": "None",
  "common.next": "next",
  "common.unknown": "Unknown",
  "common.yes": "Yes",
  "common.no": "No",
  "common.empty_title": "Nothing here yet",
  "common.empty_body": "There is nothing to show for this view.",
  "a11y.live_region": "Status updates",
  "a11y.breadcrumb": "Breadcrumb",
  "money.residual": "Residual digits beyond the currency's minor unit",
  "stage.nav_label": "Migration steps",
  "stage.connect": "Connect",
  "stage.scope": "Scope",
  "stage.review": "Review",
  "stage.dry_run": "Dry run",
  "stage.status": "Status",
  "stage.done": "done",
  "stage.current": "current",
  "stage.stopped": "stopped",
  "stage.note_extracting": "extracting",
  "stage.note_mapping": "mapping",
  "stage.note_paused": "paused",
  "check.PASS": "Pass",
  "check.FAIL": "Fail",
  "check.WARN": "Warning",
  "check.MISSING": "Missing",
  "banner.info": "Information",
  "banner.warn": "Warning",
  "banner.block": "Blocking",
  // F3: `MigrationPauseBanner` renders in `RootLayout` for every `/migrations/:id/*` screen (not gated to
  // `screens/status`, unlike `status.*`/`delta.*` — see that component's own docstring, C1), so these are
  // always-enforced rather than gated.
  "migration.paused":
    "Paused until {at}: {vendor}'s daily API limit is spent. Everything fetched so far is kept, and the migration continues from where it stopped when it next runs after that time.",
  "migration.paused_past":
    "{vendor}'s daily API limit was due to reset at {at}. If nothing is happening, run the migration again.",
  // Review UX: ONE paused presentation for the list row, the header badge and the status page.
  "migration.badge_paused": "Paused - daily limit",
  "migration.resumes_after": "resumes after {at}",
  "migration.resume_overdue": "was due to resume at {at}",
  "migration.paused_repeated": "This is pause {n} in a row at this step.",
  "table.empty": "No rows",
  "time.days_ago": "Expired {count} days ago",
  "time.hours_ago": "Expired {count} hours ago",
  "time.in_days": "in {count} days",
  "time.in_hours": "in {count} hours",
  "time.in_minutes": "in {count} min",
  "time.minutes_ago": "Expired {count} min ago",
  "time.unknown": "Unknown",
  "time.just_now": "just now",
  "time.minutes_past": "{count} minutes ago",
  "time.hours_past": "{count} hours ago",
  "time.days_past": "{count} days ago",
  "toast.request_id": "Request id: {id}",
  "toast.dismiss": "Dismiss message",
  "error.unexpected": "Something went wrong.",
  "error.xero_rate_limited": "Xero is limiting requests right now ({limit} limit). Try again in {wait}.",
  "error.xero_rate_limited_no_limit": "Xero is limiting requests right now. Try again in {wait}.",
  "error.xero_rate_limited_limit.minute": "minute",
  "error.xero_rate_limited_limit.day": "daily",
  "error.xero_rate_limited_limit.concurrent": "concurrent-request",
  "error.xero_rate_limited_limit.appminute": "app minute",
  "error.xero_rate_limited_wait_unknown": "a minute or so",
  "error.wait_second_one": "1 second",
  "error.wait_second_other": "{n} seconds",
  "error.wait_minute_one": "1 minute",
  "error.wait_minute_other": "{n} minutes",
  "error.wait_hour_one": "1 hour",
  "error.wait_hour_other": "{n} hours",
  "systems.warning_title": "Before you migrate",
  "failure.title": "This migration stopped",
  "failure.note": "What the worker recorded",
  "failure.destination_errors": "What the destination reported",
  "failure.preflight_issues": "Preflight checks that blocked this migration",
  "failure.retryable_yes": "This failure is marked retryable.",
  "failure.retryable_no": "This failure is not marked retryable.",
  "failure.request_id": "Request id of the last request to touch this migration: {id}",
  "failure.job": "Job {id}: {status}, attempt {attempts}, deferred at {deferred}",
  "failure.job_none": "No job row exists for this migration.",
  "failure.retry": "Retry",
  "failure.cancel": "Cancel migration",
  "failure.cancel_reason": "Reason (recorded on the event)",
  "failure.state_line": "State {state} · entered at {at}",
  "failure.retryable_line": "This failure is marked retryable. A retry restarts from {target}.",
  "failure.cancel_confirm": "Confirm cancellation",
  "failure.cancel_reason_hint":
    "One sentence. It is written to the migration event and cannot be edited later.",
  "failure.rolling_back": "Reversing batch {batch}. No action is available while a rollback runs.",
  "failure.rolled_back_retry": "Retrying returns this migration to loading.",
  "preflight.blocking_heading": "Blocking: fix these to continue ({count})",
  "preflight.warning_heading": "Warnings: review these ({count})",
  "preflight.says": "{vendor} says",
  "preflight.has": "{vendor} has",
  "preflight.what_to_do": "What to do",
  "preflight.code": "Check code: {code}",
  "preflight.override_non_empty": "Allow a non-empty destination and retry",
  "preflight.override_fy_roll": "Roll the year end and retry",
  "preflight.override_fy_carry": "Carry the year end and retry",
  "preflight.override_adopt": "Set these settings and retry",
  "preflight.advanced": "Advanced",
  "preflight.override_title": "Record this override and retry",
  "preflight.override_recording": "You are recording: {override}.",
  "preflight.override_body":
    "The override is recorded in the audit trail against your operator name, then the migration runs again from profiling.",
  "preflight.code.BASE_CURRENCY.title": "The two organisations use different base currencies",
  "preflight.code.BASE_CURRENCY.next":
    "A base currency cannot be changed once an organisation has transactions. Choose a destination organisation with the same base currency.",
  "preflight.code.ADOPT_SOURCE_SETTINGS.title": "These settings will be taken from the source",
  "preflight.code.ADOPT_SOURCE_SETTINGS.next":
    "Nothing has been changed yet. Choose Set these settings and retry to record your consent; LuciBook is changed once, then the migration continues.",
  "preflight.code.FY_END.title": "The financial year ends differ",
  "preflight.code.FY_END.next":
    "Make the financial year end the same on both sides (change it in the destination, or choose another destination), then retry.",
  "preflight.code.VAT_SCHEME.title": "The VAT schemes differ",
  "preflight.code.VAT_SCHEME.next":
    "Set the destination's VAT scheme to match the source in its VAT settings, then retry.",
  "preflight.code.VAT_FREQUENCY.title": "The VAT return frequencies differ",
  "preflight.code.VAT_FREQUENCY.next":
    "Check the VAT return frequency in the destination's VAT settings. This does not stop the migration.",
  "preflight.code.VAT_NUMBER.title": "The VAT numbers differ",
  "preflight.code.VAT_NUMBER.next":
    "Make sure the destination holds the same VAT number as the source, then retry.",
  "preflight.code.VAT_UNVERIFIED.title": "The source's VAT settings could not be verified",
  "preflight.code.VAT_UNVERIFIED.next":
    "Confirm the VAT scheme and return frequency by hand in the destination's VAT settings.",
  "preflight.code.VAT_REGISTRATION.title": "VAT registration differs between the two organisations",
  "preflight.code.VAT_REGISTRATION.next":
    "Register or de-register the destination for VAT so it matches the source, then retry.",
  "preflight.code.VAT_FLAT_RATE.title": "The VAT flat rate needs checking",
  "preflight.code.VAT_FLAT_RATE.next":
    "Enter or confirm the flat rate percentage in the destination's VAT settings.",
  "preflight.code.VAT_NUMBER_MISSING.title": "A VAT number is missing",
  "preflight.code.VAT_NUMBER_MISSING.next":
    "Add the VAT number to the organisation that lacks it, then retry.",
  "preflight.code.VAT_PERIOD.title": "The conversion date is inside a VAT period",
  "preflight.code.VAT_PERIOD.next":
    "A conversion date cannot be changed once checked. Cancel this migration and start a new one with a conversion date on the first day of a month.",
  "preflight.code.NOT_EMPTY.title": "The destination already holds data",
  "preflight.code.NOT_EMPTY.next":
    "Empty the destination, or record the override below to load into it anyway.",
  "preflight.code.xero_not_empty.title": "The Xero organisation already holds activity",
  "preflight.code.xero_not_empty.next":
    "Use an empty Xero organisation, or record the override below to load into it anyway.",
  "preflight.code.SOURCE_ROLE.title": "The connected user's role may not read everything",
  "preflight.code.SOURCE_ROLE.next": "Reconnect the source with a user who has adviser or standard access.",
  "preflight.code.SOURCE_ACTION_DENIED.title": "The connected user may not view reports we need",
  "preflight.code.SOURCE_ACTION_DENIED.next":
    "Reconnect the source with a user who can view the reports and manual journals named here.",
  "preflight.code.SOURCE_ACTIONS_UNKNOWN.title": "The connected user's permissions could not be checked",
  "preflight.code.SOURCE_ACTIONS_UNKNOWN.next":
    "Make sure the connected user can view reports and manual journals, or the migration may fail part-way.",
  "preflight.code.DEST_USER_ROLE.title": "The destination user cannot post balances",
  "preflight.code.DEST_USER_ROLE.next": "Reconnect the destination with an owner or admin user.",
  "preflight.code.PLAN_TIER.title": "The source plan could not be identified",
  "preflight.code.PLAN_TIER.next": "Confirm the source plan includes reports and journals.",
  "preflight.code.SOURCE_PLAN_TIER.title": "The source plan cannot supply what a migration needs",
  "preflight.code.SOURCE_PLAN_TIER.next": "Upgrade the source plan, or choose another source.",
  "preflight.code.DEST_LOCK_DATE.title": "The destination is locked past the conversion date",
  "preflight.code.DEST_LOCK_DATE.next":
    "Move the destination's lock date to before the conversion date, then retry.",
  "preflight.code.SOURCE_LOCK_DATE.title": "The source is locked later than the day before conversion",
  "preflight.code.SOURCE_LOCK_DATE.next":
    "Make sure nobody edits the source before the conversion date. Extraction reads whatever is there.",
  "preflight.code.SOURCE_NOT_LOCKED.title": "The source is not locked up to the conversion date",
  "preflight.code.SOURCE_NOT_LOCKED.next":
    "Set the lock date in {vendor} to the day before the conversion date, then retry.",
  "preflight.code.CONVERSION_AT_FY_END.title": "The conversion date follows your financial year end",
  "preflight.code.CONVERSION_AT_FY_END.next":
    "Nothing to do: the year's profit and loss is rolled into retained earnings by default. Under Advanced you can carry it across exactly as the source shows it instead.",
  "preflight.code.qbo_grant_expiring.title": "The QuickBooks connection expires soon",
  "preflight.code.qbo_grant_expiring.next": "Reconnect QuickBooks before the cutover.",
  "preflight.code.qbo_grant_too_short.title": "The QuickBooks connection expires too soon",
  "preflight.code.qbo_grant_too_short.next": "Reconnect QuickBooks, then start the migration again.",
  "preflight.code.xero_locked_period.title": "Xero is locked past the conversion date",
  "preflight.code.xero_locked_period.next":
    "Ask a person with adviser access to clear the lock date in Xero, then retry.",
  "preflight.code.xero_plan_tier_too_low.title": "The Xero plan is too small for this load",
  "preflight.code.xero_plan_tier_too_low.next": "Move the Xero organisation to Grow or above, then retry.",
  "preflight.code.xero_tracking_cap.title": "Too many tracking categories for Xero",
  "preflight.code.xero_tracking_cap.next": "Choose which two survive in mapping review.",
  "preflight.code.xero_tracking_option_cap.title": "A tracking category has too many options for Xero",
  "preflight.code.xero_tracking_option_cap.next": "Reduce the category to 100 options or fewer.",
  "preflight.code.xero_bank_lines_dropped.title": "Bank statement lines will not travel to Xero",
  "preflight.code.xero_bank_lines_dropped.next": "No action needed. Reconcile from the new feed.",
  "preflight.code.xero_assets_draft_only_no_rollback.title": "Fixed assets arrive in Xero as drafts",
  "preflight.code.xero_assets_draft_only_no_rollback.next":
    "No action needed now. If the migration is rolled back you delete the drafts by hand.",
  "preflight.code.xero_conversion_date_not_month_start.title":
    "Xero needs the conversion date on the first of a month",
  "preflight.code.xero_conversion_date_not_month_start.next":
    "Cancel this migration and start a new one with a first-of-month conversion date.",
  "preflight.code.xero_contact_number_duplicate.title": "Xero holds contacts with the same contact code",
  "preflight.code.xero_contact_number_duplicate.next": "Make the contact codes unique in Xero, then retry.",
  "preflight.code.xero_contact_name_collision.title": "A contact name already exists in Xero",
  "preflight.code.xero_contact_name_collision.next":
    "Review the contact named here in Xero. A same-named active contact would be merged.",
  "preflight.code.xero_write_scopes_missing.title": "The Xero connection cannot write",
  "preflight.code.xero_write_scopes_missing.next":
    "Re-authorise the Xero connection with write access, then retry.",
  "preflight.code.JOURNAL_TO_CONTROL.title": "A journal posts to a control account",
  "preflight.code.JOURNAL_TO_CONTROL.next":
    "Open items must travel as documents. Review the journal named here.",
  "preflight.code.CONTACT_NAME_SUFFIXED.title": "Some contact names were changed to stay unique",
  "preflight.code.CONTACT_NAME_SUFFIXED.next": "No action needed. Review the renamed contacts afterwards.",
  "preflight.code.TAX_CODE_UNMAPPED.title": "A tax code has no equivalent in the destination",
  "preflight.code.TAX_CODE_UNMAPPED.next": "Map it in the tax mapping review.",
  "preflight.code.OPENING_BALANCE_EQUITY_MISSING.title":
    "The destination has no Opening Balance Equity account",
  "preflight.code.OPENING_BALANCE_EQUITY_MISSING.next": "Create it in the destination, then retry.",
  "preflight.code.PLAN_TIER_TOO_LOW.title": "The destination plan is too small",
  "preflight.code.PLAN_TIER_TOO_LOW.next": "Upgrade the destination plan, then retry.",
  "preflight.code.ATTACHMENT_OVERSIZE.title": "An attachment is too large for the destination",
  "preflight.code.ATTACHMENT_OVERSIZE.next": "No action needed. Oversize files are skipped and listed.",
  "preflight.code.ATTACHMENT_TYPE.title": "An attachment type is not accepted by the destination",
  "preflight.code.ATTACHMENT_TYPE.next": "No action needed. Those files are skipped and listed.",
  "preflight.code.freeagent_accountant_carve_outs.title": "The FreeAgent user is an Accountant",
  "preflight.code.freeagent_accountant_carve_outs.next":
    "No action needed for the migration. Check what that role cannot do before cutover.",
  "preflight.code.freeagent_insufficient_level.title": "The FreeAgent user's permission level is too low",
  "preflight.code.freeagent_insufficient_level.next": "Reconnect with a FreeAgent user who has full access.",
  "preflight.code.freeagent_page_count_mismatch.title": "FreeAgent changed while we were reading",
  "preflight.code.freeagent_page_count_mismatch.next": "No action needed. It is re-checked before sign-off.",
  "preflight.code.sage50_audit_trail_does_not_tie.title":
    "The Sage 50 audit trail does not tie to the trial balance",
  "preflight.code.sage50_audit_trail_does_not_tie.next": "Check the Sage 50 data, then retry.",
  "preflight.code.sage50_fx_rate_underivable.title": "A Sage 50 exchange rate could not be worked out",
  "preflight.code.sage50_fx_rate_underivable.next":
    "Check the transaction named here in Sage 50, then retry.",
  "preflight.code.sage50_journal_grouping_ambiguous.title": "Sage 50 journals could not be grouped safely",
  "preflight.code.sage50_journal_grouping_ambiguous.next": "Check the Sage 50 export, then retry.",
  "preflight.code.sage50_probe_field_unavailable.title": "The Sage 50 helper could not read a required field",
  "preflight.code.sage50_probe_field_unavailable.next": "Update the Sage 50 helper, then retry.",
  "preflight.code.sage50_tb_leg_unavailable.title": "A Sage 50 trial balance leg is unavailable",
  "preflight.code.sage50_tb_leg_unavailable.next": "No action needed. The check is repeated at verification.",

  // ---------------------------------------------------------------- dynamic families (exempt from "referenced")
  "state.draft": "Draft",
  "state.connecting": "Connecting",
  "state.profiling": "Profiling",
  "state.scoping": "Scoping",
  "state.awaiting_scope_signoff": "Awaiting scope sign-off",
  "nextstep.ready": "The migration has moved on: the {step} step is ready for you.",
  "nextstep.go": "Go to {step}",
  "state.extracting": "Extracting",
  "state.mapping": "Mapping",
  "state.awaiting_mapping_review": "Awaiting mapping review",
  "state.transforming": "Transforming",
  "state.dry_running": "Dry running",
  "state.awaiting_conversion_signoff": "Awaiting conversion sign-off",
  "state.loading": "Loading",
  "state.verifying": "Verifying",
  "state.hypercare": "Hypercare",
  "state.locked": "Locked",
  "state.failed": "Failed",
  "state.cancelled": "Cancelled",
  "state.rolling_back": "Rolling back",
  "state.rolled_back": "Rolled back",
  "severity.BLOCK": "Blocking",
  "severity.WARN": "Warning",
  "severity.INFO": "Note",
  "support.FULL": "Full",
  "support.PARTIAL": "Partial",
  "support.NONE": "Not supported",
  "revoke.active": "Active",
  "revoke.revoked": "Revoked",
  "revoke.revoke_failed": "Access may still be live in {vendor}",
  "revoke.revoke_failed_short": "Revoke failed",
  "signer_role.DIRECTOR": "Director",
  "signer_role.OWNER": "Owner",
  "signer_role.AGENT": "Agent",
  "signer_role.BOOKKEEPER": "Bookkeeper",
  "job.todo": "Queued",
  "job.doing": "Running",
  "job.succeeded": "Succeeded",
  "job.failed": "Failed",
  "job.cancelled": "Cancelled",
  "job.aborting": "Aborting",
  "job.aborted": "Aborted",
  "error.code.unauthorized": "Your session has expired. Sign in again.",
  "error.code.csrf_failed": "The request could not be verified. Reload the page and try again.",
  "error.code.forbidden": "That action is not allowed.",
  "error.code.not_found": "That record does not exist.",
  "error.code.validation_failed": "The service rejected the values submitted.",
  "error.code.illegal_transition": "That step is not available from the current state.",
  "error.code.scope_frozen": "The scope was frozen at sign-off and cannot be changed.",
  "error.code.blocking_reviews_remain": "{count} blocking review items are still unresolved.",
  "error.code.package_changed": "The package changed since this page was loaded.",
  "error.code.already_resolved": "Someone resolved this item first.",
  "error.code.connection_in_use": "That connection is used by a migration that has not finished.",
  "error.code.migration_busy": "This migration is already doing something. Try again shortly.",
  "error.code.not_in_state": "The migration is no longer in a state that allows that.",
  "error.code.idempotency_mismatch": "That request was already made with different values.",
  "error.code.oauth_state_invalid": "That authorisation link is no longer valid.",
  "error.code.oauth_denied": "Authorisation was declined in Xero.",
  "error.code.payload_too_large": "That file is too large to download here. Use the command line.",
  "error.code.gone": "That evidence pack passed its retention date and was deleted.",
  "error.code.job_defer_failed": "The work queue did not accept the job. Nothing was started.",
  "error.code.rate_limited": "Too many attempts. Wait and try again.",
  "error.code.internal": "The service reported an internal error.",

  // ---------------------------------------------------------------- src/screens/operator (06b)
  "login.title": "Sign in",
  "login.field": "Operator API key",
  "login.submit": "Sign in",
  "login.failed": "That key was not accepted.",
  "login.throttled": "Too many attempts. Wait {seconds} seconds.",
  // Fable #8: belt-and-braces escape hatch for a visitor who lands here (a bookmark, a stale link) on a
  // multiuser deployment that has no operator key configured -- this form cannot work for them.
  "login.multiuser_hint": "Sign in with email and password",
  "nav.migrations": "Migrations",
  "nav.connections": "Connections",
  "nav.settings": "Settings",
  "nav.practice": "Practice",
  "nav.security": "Security",
  "nav.sign_out": "Sign out",
  "nav.primary": "Primary",
  "nav.theme_to_dark": "Switch to dark mode",
  "nav.theme_to_light": "Switch to light mode",
  "operator.eyebrow": "Workspace",
  "operator.title": "Migrations",
  "operator.caption": "Every migration, newest first.",
  "operator.col_migration": "Migration",
  "operator.col_route": "Source → destination",
  "operator.col_state": "State",
  "operator.col_conversion": "Conversion date",
  "operator.col_attempt": "Attempt",
  "operator.col_blocking": "Blocking",
  "operator.col_updated": "Updated",
  "operator.filter_state": "Filter by state",
  "operator.filter_all": "All states",
  "operator.filter_source": "Source system",
  "operator.filter_destination": "Destination system",
  "operator.filter_all_systems": "All systems",
  "operator.next_page": "Next page",
  "operator.prev_page": "Previous page",
  "operator.empty": "No migrations match this filter.",
  "operator.open": "Open {id}",
  "operator.route_to": "to",
  "migrationheader.meta_route": "{source} → {destination}",
  "migrationheader.meta_scope": "{source} → {destination} · conversion {date} · {currency}",
  "migrationheader.meta_status": "Attempt {n} · updated {when}",
  "migrations.wizard.start_button": "Start new migration",
  "migrations.wizard.empty_title": "No migrations yet",
  "migrations.wizard.empty_body":
    "Connect a source and a destination system, then create your first migration.",
  "migrations.wizard.empty_step_source": "Connect a source",
  "migrations.wizard.empty_step_destination": "Connect a destination",
  "migrations.wizard.empty_step_create": "Create the migration",
  "migrations.wizard.empty_step_connected": "{vendor} connected",
  "migrations.wizard.empty_step_source_sub": "Any accounting system",
  "migrations.wizard.empty_step_destination_sub": "Any system, LuciBook included",
  "migrations.wizard.empty_step_create_sub": "Scope, review, dry run",
  "connections.title": "Connections",
  "connections.caption": "Every connection, of every system.",
  "connections.col_system": "System",
  "connections.col_organisation": "Organisation",
  "connections.col_status": "Status",
  // The access token lives 30 minutes and is renewed on next use, so its expiry is only ever a tooltip. This
  // column says whether the connection will keep renewing itself, which depends on the REFRESH token.
  "connections.col_expires": "Sign-in",
  "connections.col_refresh_expires": "Refresh expires",
  "connections.renews_automatically": "Renews automatically",
  "connections.needs_reconnect": "Needs reconnect",
  "connections.access_token_tooltip":
    "Access token: {when}. It is renewed automatically the next time it is used.",
  "connections.refresh_renews_on_use": "Renews on use",
  "connections.show_history": "Show history",
  "connections.status_superseded": "Superseded",
  "connections.superseded_note": "Replaced by a newer connection for this organisation. Kept as history.",
  "connections.col_actions": "Actions",
  "connections.status.connected": "Connected",
  "connections.status_active": "Active",
  "connections.status.reconnect_needed": "Needs reconnect",
  // XMG-SEC C1: the vendor itself took the connection away (Xero refused the stored sign-in, or no longer lists
  // the organisation). `status_disconnected` names the vendor; the dynamic key is the generic fallback.
  "connections.status.disconnected": "Disconnected",
  "connections.status_disconnected": "Disconnected in {vendor}",
  "connections.revoke": "Revoke",
  "connections.revoke_again": "Try revoking again",
  "connections.revoke_confirm_title": "Revoke this connection?",
  "connections.revoke_confirm_body":
    "LuciMigration deletes its stored credentials for {name} once the revoke succeeds, or once it fails for a reason that means those credentials could never work again. If it fails for a temporary reason instead (a network error, for example), the credentials are kept so this can be tried again.",
  // Round 7 (Opus verification review of round 6, LOW): `revoke_confirm_body` above is only true for
  // Xero and LuciBook - the two systems `revoke_connection_task`/`stage_lock` actually call out to and
  // retry after a refresh (§A6.9). Every OTHER system (QBO, FreeAgent, Sage BC, HMRC, `fake`) has no
  // revoke path built at all: clicking "Revoke" there makes no vendor call whatsoever and always deletes
  // LuciMigration's own copy unconditionally - `revoke_confirm_body`'s "kept so this can be tried again"
  // clause would be false for them.
  "connections.revoke_confirm_body_no_vendor_call":
    "LuciMigration deletes its stored credentials for {name}. {vendor} is never contacted - there is no automatic revoke built for this system yet - so this always deletes LuciMigration's own copy, whatever else happens. Remove access directly in {vendor}'s own connected-apps or integrations settings to be certain it is gone there too.",
  "connections.lucibook_reconnect": "Reconnect with LuciBook",
  "connections.xero_reconnect": "Reconnect Xero",
  // The redesign's revoke banner (Connections.html): title is `revoke.revoke_failed`, this is the body and
  // the action is "Try revoking again". It renders only for genuinely RETRYABLE failures
  // (`revoke_retryable`) - a network error, a 5xx, a 429, a timeout - never a dead/expired token, which is
  // the PERMANENT case that gets the unretryable detail below instead.
  "connections.revoke_banner_body":
    "LuciMigration kept its stored credentials so a retry can really contact {vendor} again, but {vendor} has not confirmed the revocation yet.",
  "connections.revoke_failed_unretryable_detail":
    "LuciMigration's stored credentials for this {vendor} connection are already deleted, but {vendor} was never confirmed to have revoked the connection - access may still be live. Remove {holder}'s access directly in {vendor}'s Connected Apps / integrations settings to be certain it is gone.",
  // Round 6 (Opus verification review of round 5, MEDIUM): unlike the banner above - which only
  // ever renders AFTER a real revoke_failed outcome, where "kept" is already true - this renders in the
  // confirm dialog BEFORE any revoke has run at all, so it must describe both possible outcomes
  // conditionally rather than asserting the retryable one as fact (round 5 claimed "kept" pre-emptively,
  // contradicting `revoke_confirm_body`'s own first paragraph just above it).
  "connections.revoke_confirm_forced_refresh_caveat":
    "The stored access token can already be dead by the time this runs, and only one forced refresh is attempted before giving up. If that fails for a reason that still leaves the credentials usable, LuciMigration keeps them so trying again can really make a new attempt; if it fails for a reason that means they never will work again, LuciMigration deletes them instead.",
  // Round 6 (Opus verification review of round 5, LOW): the terminal action for a `revoke_failed` row
  // with no stored credentials left - it has no way to contact the vendor again.
  //
  // Round 7 (Opus verification review of round 6, LOW): this used to say "Remove from list", which is
  // false on both halves - the web fetches `include_revoked: true` (`api/hooks.ts::useConnections`), so
  // the row never actually leaves the list, and the click makes NO vendor call at all (no stored
  // credentials left to send one with). Renamed to say exactly what happens - LuciMigration's own local
  // record is marked revoked, nothing more - and the confirm dialog (`revoke_mark_revoked_confirm_*`
  // below) now says the same before the click, not just after.
  "connections.mark_revoked": "Mark as revoked",
  "connections.mark_revoked_confirm_title": "Mark this connection as revoked?",
  // Round 7 (Opus verification review of round 6, LOW): stated up front, before the click, not just in
  // the per-row detail text that only appears afterwards - this row has no stored credentials LuciMigration
  // could use to contact {vendor} even if it tried, so this action makes NO vendor call at all. It only
  // updates LuciMigration's own local record.
  "connections.mark_revoked_confirm_body":
    "This only updates LuciMigration's own local record for {name} - it makes no call to {vendor} at all, because there are no stored credentials left to make one with. If {vendor}'s own access for this connection is still live, remove it directly in {vendor}'s Connected Apps / integrations settings.",
  // T4 revoke r7 LOW: `mark_revoked_confirm_body` above is only true when the blob is genuinely gone - a
  // row whose blob EXISTS but cannot be decrypted with the key running now (`ConnectionOut.revoke_blob_
  // undecryptable`) still has no vendor call to make, but "there are no stored credentials left" is false
  // for it: the credentials are still on file, merely unreadable. This is the same distinction `_to_out`'s
  // own synthesized `revoke_warning` already makes for the per-row detail text - this key says the
  // identical truth in the confirm dialog, before the click.
  "connections.mark_revoked_confirm_body_blob_undecryptable":
    "This only updates LuciMigration's own local record for {name} - it still makes no call to {vendor}, because the stored credentials for this connection cannot be read with the key running now. If {vendor}'s own access for this connection is still live, remove it directly in {vendor}'s Connected Apps / integrations settings.",
  "connections.empty": "No connections yet.",
  "connections.add": "Add connection",
  "connections.add_title": "Add a connection",
  "connections.add_back": "Back to connections",
  "connections.add_intro":
    "Connect each accounting system once. Any connection can be picked as the source or the destination of a migration.",
  "connections.systems_heading": "Accounting systems",
  "connections.connect_short": "Connect",
  "connections.coming_soon": "Coming soon",
  "connections.xero_name": "Xero",
  "connections.xero_blurb": "Accounts, transactions and journals.",
  "connections.xero_connect": "Connect to Xero",
  "connections.xero_connected": "Xero connection created.",
  "connections.lucibook_name": "LuciBook",
  "connections.lucibook_blurb": "Connect a LuciBook organisation via OAuth.",
  "connections.lucibook_oauth_connect": "Connect with LuciBook",
  "connections.lucibook_oauth_connected": "LuciBook connection created.",
  "connections.sage50_name": "Sage 50",
  "connections.sage50_blurb":
    "Requires the LuciBridge Windows agent, installed on the machine running Sage 50.",
  "connections.sage50_badge": "Desktop agent",
  "connections.sage50_download": "Download agent",
  "connections.sage50_note": "The installer link is coming soon.",
  "connections.fake_tag": "DEV",
  "connections.fake_name": "Fake connector",
  "connections.fake_blurb": "Development only. Creates a demo connection for testing the console.",
  "connections.fake_source": "Create demo source",
  "connections.fake_destination": "Create demo destination",
  "connections.fake_created": "Demo connection created.",
  "connections.qbo_name": "QuickBooks Online",
  "connections.qbo_blurb": "Accounts, transactions and journals.",
  "connections.qbo_connect": "Connect to QuickBooks Online",
  "connections.qbo_connected": "QuickBooks Online connection created.",
  "connections.freeagent_name": "FreeAgent",
  "connections.freeagent_blurb": "Accounts, transactions and journals.",
  "connections.freeagent_connect": "Connect to FreeAgent",
  "connections.freeagent_connected": "FreeAgent connection created.",
  "connections.sagebc_name": "Sage Business Cloud",
  "connections.sagebc_blurb": "Accounts, transactions and journals.",
  "connections.sagebc_connect": "Connect to Sage Business Cloud",
  "connections.sagebc_connected": "Sage Business Cloud connection created.",
  "connections.hmrc_name": "HMRC MTD VAT",
  "connections.hmrc_blurb": "Not yet available from this screen.",
  "connections.oauth_not_configured":
    "This connector requires the operator to set {prefix}_CLIENT_ID and {prefix}_CLIENT_SECRET in the deployment config.",
  "connections.oauth_connected_generic": "{system} connection created.",
  "settings.title": "Settings",
  "settings.switches": "Connector switches",
  "settings.switches_caption": "Kill switches, per system, entity and direction.",
  "settings.col_system": "System",
  "settings.col_entity": "Entity",
  "settings.col_direction": "Direction",
  "settings.col_enabled": "Enabled",
  "settings.col_reason": "Reason",
  "settings.switch_reason": "Reason for this change",
  "settings.switch_reason_hint":
    "At least three characters. A kill switch without a reason is an outage nobody can explain.",
  "settings.switch_apply": "Apply",
  "settings.switch_toggle": "Toggle {system} {entity} {direction}",
  "settings.sections": "Settings sections",
  "settings.read_only": "Read-only",
  "settings.alerts": "Vendor alerts",
  "settings.limits": "Vendor limits",
  "settings.col_limit": "Limit",
  "settings.col_why": "Why",
  "settings.limits_show_detail": "Show technical detail",
  "settings.limits_detail_caption": "Raw `vendor_limits` rows, for debugging.",
  "settings.purge": "Retention sweep",
  "settings.purge_dry_run": "Dry run (show what would be purged, change nothing)",
  "settings.purge_run": "Run sweep",
  "settings.purge_counts": "What the sweep would remove",
  "settings.purge_started": "The sweep was accepted and runs in the background.",
  "settings.col_count": "Rows",
  "settings.col_target": "Target",
  "settings.col_severity": "Severity",
  "settings.col_message": "Message",
  "settings.col_when": "When",
  "settings.no_switches": "No connector switches are set.",
  "settings.no_alerts": "No vendor alerts.",
  "settings.no_limits": "No vendor limits recorded.",
  "router.not_found": "That page does not exist.",
  "router.not_found_home": "Go to the migrations list",

  // ---------------------------------------------------------------- src/screens/connect (07)
  "connect.title": "Connect the two systems",
  "connect.source": "Source",
  "connect.destination": "Destination",
  "connect.step_1of4": "Step 1 of 4",
  "connect.step_2of4": "Step 2 of 4",
  "connect.already_source": "Already the source",
  "connect.meta_connected": "Connected {when}",
  "connect.rail_label": "New migration steps",
  "connect.rail_source": "Source connection",
  "connect.rail_destination": "Destination connection",
  "connect.rail_basis": "Conversion basis",
  "connect.rail_preview": "Preview & confirm",
  "connect.breadcrumb": "New migration",
  "connect.hmrc_title": "HMRC VAT (Making Tax Digital)",
  "connect.hmrc_vrn": "VAT registration number",
  "connect.hmrc_vrn_hint": "Nine digits, no spaces. HMRC grants authority over one VAT number at a time.",
  "connect.hmrc_vrn_invalid": "A VAT registration number is nine digits.",
  "connect.hmrc_submit": "Continue to HMRC",
  "connect.hmrc_continue": "Go to HMRC and sign in",
  "connect.continue": "Continue to consent",
  "connect.add_source_link": "Add a source connection",
  "connect.add_destination_link": "Add a destination connection",
  "connect.same_system_error":
    "Source and destination are both {vendor}. A migration moves data from one kind of system into another, so choose a different system for one side.",
  "connect.lucibook_org_fallback": "LuciBook organisation {org_id}",
  "oauth.return_eyebrow": "Authorisation received",
  "oauth.return_title": "Choose the Xero organisation",
  "oauth.name_label": "Name for this connection",
  "oauth.name_hint": "How this connection is listed. You can use the client's name.",
  "oauth.tenant_legend": "Organisations this authorisation covers",
  "oauth.tenant_option": "{name} ({id})",
  "oauth.select": "Use this organisation",
  "oauth.missing_scopes": "Xero did not grant every permission this migration needs: {scopes}",
  "oauth.reconsent": "Ask Xero again",
  "oauth.state_invalid": "That authorisation link is no longer valid. Nothing was connected.",
  "oauth.connection_mismatch":
    "LuciBook couldn't confirm which organisation this was for. Nothing was connected — try connecting again.",
  // CB2 round 2: these five codes are the LuciBook callback's own choke-point outcomes
  // (`connections.py::lucibook_oauth_callback`'s docstring table) that are not covered by `oauth.denied.*` or
  // `oauth.connection_mismatch` above. Each needs its own toast so `vendor_unavailable` (a LuciBook outage,
  // where the state is un-consumed and a retry would work) is never told to the operator as "link expired".
  "oauth.vendor_unavailable":
    "{system} could not be reached just now. Nothing was connected — try connecting again in a moment.",
  // Round 3, item 5 (LOW): a PERMANENT vendor rejection (`PermanentConnectorError`) is split out of
  // `vendor_unavailable` above - "could not be reached ... try again in a moment" is simply false for a
  // vendor that WAS reached and said no. Never promises a specific fix (this callback cannot tell whether
  // it was a stale credential, a revoked scope, or something else on LuciBook's side).
  "oauth.vendor_rejected": "{system} rejected the connection. Nothing was connected — try connecting again.",
  // Round 3, item 5 (LOW): must never promise the SAME authorisation resumes - it cannot (§ the callback's
  // own docstring: the one-time `code` is already dead by the time this redirect fires, and no `next=`
  // carries this exact callback forward). "to continue" in the old copy read as a resume promise; this
  // spells out the two, unambiguous, separate steps instead.
  "oauth.session_expired":
    "Your session expired while you were connecting. Sign in, then connect LuciBook again.",
  "oauth.operator_mismatch":
    "This authorisation was started by a different signed-in operator. Nothing was connected.",
  "oauth.internal_error": "Something went wrong while connecting. Nothing was connected — please try again.",
  // Fable F3/F6: `oauth.denied` used to be one Xero-hardcoded sentence for every vendor and every reason.
  // `oauthCopy.ts::oauthDeniedMessage` picks one of these by the raw `oauth_error` code the callback now
  // passes through (`connections.py::OAUTH_ERROR_CODES`), with `{system}` filled in from `system=<vendor>`.
  "oauth.denied.access_denied": "{system} declined the authorisation. Nothing was connected.",
  "oauth.denied.invalid_scope":
    "{system} did not grant every permission this migration requested. Nothing was connected.",
  "oauth.denied.invalid_request": "{system} rejected that authorisation request. Nothing was connected.",
  "oauth.denied.unauthorized_client":
    "{system} does not allow this application to connect. Nothing was connected.",
  "oauth.denied.redirect_uri_mismatch":
    "{system} rejected the return address for this authorisation. Nothing was connected.",
  "oauth.denied.server_error":
    "{system} reported an internal error during authorisation. Nothing was connected.",
  "oauth.denied.temporarily_unavailable": "{system} is temporarily unavailable. Nothing was connected.",
  "oauth.denied.unknown": "Authorisation was declined in {system}. Nothing was connected.",
  "oauth.denied_vendor_fallback": "the connector",
  "oauth.start_again": "Start again",
  "oauth.no_handoff": "This page was opened without an authorisation result.",

  // Critique addendum C4: the HMRC OAuth callback's own return route.
  "hmrc_return.title": "HMRC connection",
  "hmrc_return.connected": "HMRC connected.",
  "hmrc_return.back_to_migration": "Back to migration",
  "hmrc_return.home": "Go home",

  // ---------------------------------------------------------------- src/screens/scope (07)
  "consent.title": "Conversion basis and consent",
  "consent.basis": "Conversion basis",
  "consent.conversion_date": "Conversion date",
  "consent.reading_source": "Reading the organisation from {vendor}.",
  "consent.date_label": "Conversion date",
  "consent.date_from_source_lock": "From your {vendor} lock date {lock}.",
  "consent.date_from_destination": "From {destination}'s organisation settings.",
  "consent.date_destination_update": "{destination}'s conversion date will be updated from {from} to {to}.",
  "consent.check_again": "Check again",
  "consent.checking": "Checking...",
  "consent.date_blocked_title": "The conversion date cannot be worked out yet",
  "consent.date_blocked.no_lock_date":
    "{vendor} has no lock date set. Lock your {vendor} books to a month end, then come back.",
  "consent.date_blocked.lock_not_month_end":
    "Your {vendor} books are locked to {lock}, which is not a month end. Lock them to a month end, then come back.",
  "consent.date_blocked.source_unreadable": "{vendor} could not be read just now. Try again in a moment.",
  "consent.date_blocked.destination_locked":
    "{destination} is locked past the date this migration would convert on. Ask its owner to move the lock date earlier, then come back.",
  "consent.date_blocked.no_conversion_date":
    "Neither {vendor} nor {destination} has a conversion date to start from. Set the conversion date in {destination}'s organisation settings, then come back.",
  "consent.advanced": "Advanced",
  "consent.advanced_hint":
    "Only needed if you must convert from a different date than the one above. The usual pre-migration checks still apply.",
  "consent.date_override": "Conversion date override",
  "consent.date_overridden": "You are overriding the automatic date ({auto}).",
  "consent.base_currency_from_source": "{currency} — from {vendor}",
  "consent.base_currency_manual_hint": "{vendor} did not give us its base currency. Choose it from the list.",
  "consent.controller_name_hint": "Pre-filled from {vendor}. You can change it.",
  "consent.not_set": "not set",
  "consent.base_currency": "Base currency",
  "consent.legend": "Consent to process this data",
  "consent.controller_name": "Controller's legal name",
  "consent.signer": "Signer's name",
  "consent.signer_hint":
    "Filled in from your sign-in. It is recorded as the signer of this consent, so change it if someone else is signing.",
  "consent.signer_role": "Signer's role",
  "consent.terms_version": "Terms version",
  "consent.checkbox":
    "I confirm I am authorised to instruct this migration and that the controller consents to the processing described in the terms above.",
  "consent.submit": "Create the migration",
  "consent.missing_connections": "Both connections must exist before a migration can be created.",
  "consent.back": "Back to source and destination",
  "consent.preview_eyebrow": "Step 4 of 4 · Preview",
  "consent.preview_title": "What will be created",
  "consent.preview_consent": "Consent",
  "consent.preview_consent_pending": "Not yet given",
  "consent.preview_consent_given": "Given",
  "consent.fy_roll_explain":
    "Last year's profit and loss is closed to retained earnings at {opening}; the new year starts clean on {start}.",
  "consent.fy_carry_explain":
    "Last year's profit and loss is carried across as opening balances exactly as {vendor} shows it.",
  "consent.fy_policy_legend": "Financial year end policy",
  "consent.fy_policy_roll": "Roll it into retained earnings (default)",
  "consent.fy_policy_carry": "Carry the profit and loss accounts across as {vendor} shows them",
  "consent.adopt_heading": "Settings taken from {vendor}",
  "consent.adopt_note":
    "Nothing in {destination} is changed until you create the migration. These settings are the ones {vendor} holds.",
  "scope.title": "Scope",
  "scope.tabs_label": "Scope sections",
  "scope.tab_scope": "Scope",
  "scope.tab_fx": "Opening balances",
  "scope.profiles": "Organisation profiles",
  "scope.profile_source": "Source",
  "scope.profile_destination": "Destination",
  "scope.profile_base_currency": "Base currency",
  "adopt.intro": "These settings will be set on {destination} from {vendor}:",
  "adopt.fy_end": "Financial year end {source} (now {destination})",
  "adopt.vat_scheme": "VAT: {source} (now {destination})",
  "adopt.vat_number": "VAT number {source} (now {destination})",
  "adopt.vat_filing_frequency": "VAT filing frequency {source} (now {destination})",
  "adopt.frequency.MONTHLY": "Monthly",
  "adopt.frequency.QUARTERLY": "Quarterly",
  "adopt.none": "none",
  "adopt.scheme.ACCRUAL": "Standard accounting",
  "adopt.scheme.CASH": "Cash accounting",
  "adopt.scheme.FLAT_RATE_ACCRUAL": "Flat rate scheme",
  "adopt.scheme.NONE": "off",
  "adopt.refused":
    "{destination} did not accept these settings ({code}), so nothing was changed and the original differences are listed below.",
  "scope.profile_fy_end": "Financial year end",
  "scope.profile_vat_number": "VAT number",
  "scope.profile_vat_scheme": "VAT scheme",
  "scope.profile_vat_frequency": "VAT return frequency",
  "scope.profile_flat_rate": "Flat rate",
  "scope.profile_lock_dates": "Lock dates",
  "scope.profile_role": "Role",
  "scope.profile_plan_tier": "Plan tier",
  "scope.profile_is_empty": "Ledger is empty",
  "scope.preflight": "Preflight checks",
  "scope.preflight_caption": "Every check that disagrees between the two systems.",
  "scope.col_severity": "Severity",
  "scope.col_field": "Field",
  "scope.col_check": "Check",
  "scope.col_source": "Source value",
  "scope.col_destination": "Destination value",
  "scope.col_reason": "Why it matters",
  "scope.preflight_clear": "No preflight check is blocking or warning.",
  "scope.blocked":
    "Sign-off is blocked: {count} preflight checks must be resolved in the named system first.",
  "scope.basis": "Conversion basis",
  "scope.history_tier": "History tier",
  "scope.history_tier_t0": "T0 — opening balances only",
  "scope.history_tier_disabled": "Phase 2",
  "scope.entities": "Entities",
  "scope.entities_caption": "What this pair of connectors can carry.",
  "scope.entity_include": "Include {entity}",
  "scope.overrides": "Overrides",
  "scope.allow_non_empty": "Allow a destination ledger that is not empty",
  "scope.allow_non_empty_sub": "Recorded against your name.",
  "scope.attachments": "Migrate file attachments too",
  "scope.accept_warnings": "Accept the outstanding warnings",
  "scope.allow_unbalanced": "Allow an unbalanced opening package",
  "scope.override_actor": "Recorded against {actor}",
  "scope.fy_end_policy": "Financial year end policy",
  "scope.fy_end_default_hint":
    "If you choose nothing, a conversion at the financial year end rolls the year's profit and loss into retained earnings.",
  "scope.fy_end_roll": "Roll the year end forward",
  "scope.fy_end_carry": "Carry the source's year end",
  "scope.frozen": "This scope was frozen at sign-off. It is shown read-only.",
  "scope.not_active": "This step is not active yet. It is shown read-only.",
  "scope.start": "Start this migration",
  "scope.not_started":
    "This migration has not been started yet. Starting it connects to both systems and builds the profile.",
  "scope.signoff": "Sign off the scope",
  "scope.signoff_hash": "Scope hash to be frozen",
  "scope.signoff_year_end": "Year end",
  "scope.signoff_consent_label": "Consent",
  "scope.signoff_blocked_hint": "Resolve {count} blocking check(s) first.",
  "scope.blocking_items_title": "Blocking items to answer",
  "scope.blocking_items_note":
    "These review items stop the scope from being signed off. Answer each one here; the sign-off unlocks when none is left.",
  "scope.signoff_blocking_one": "1 blocking item must be answered first.",
  "scope.signoff_blocking_many": "{count} blocking items must be answered first.",
  "scope.signoff_blocking_link": "Show them",
  "scope.signoff_blocking_fx_link": "Open the FX opening balances tab",
  "scope.signoff_error_title": "The scope was not signed off",
  "scope.signoff_note":
    "Signing freezes the scope and its hash against your name. Extraction starts immediately.",
  "scope.signoff_consent": "Consent recorded for {controller} by {signer} ({role}) on {date}",
  "scope.signoff_confirm_title": "Sign off this scope?",
  "scope.signoff_confirm_body":
    "The scope and its hash are frozen and recorded against your name. Extraction starts immediately.",
  "scope.signoff_done": "Signed off. Hash {hash}.",
  "scope.signoff_closed_done": "This scope is signed off and frozen. There is nothing more to sign here.",
  "scope.signoff_closed_pending": "Sign-off opens once profiling and scoping have finished.",
  "scope.signoff_closed": "This step is closed. There is nothing to sign here.",
  "scope.saved": "Saved.",
  "scope.save": "Save scope",
  "scope.auto_approve_mapping": "Approve the mapping automatically",
  "scope.extracted_as_at": "Extracted as at",
  "scope.source_params": "Source connector parameters (JSON)",
  "scope.destination_params": "Destination connector parameters (JSON)",
  "scope.params_hint": "A JSON object. It is passed to the connector unchanged.",
  "scope.params_invalid": "That is not a JSON object.",

  // ------------------------------------------------------- scope.unreconciled_* (MT-17 design §5, §6.8)
  "scope.unreconciled_title": "Unreconciled bank activity",
  // Verbatim identical to `workflow.source_unreconciled.SEMANTICS` (Python), which is also the OpenAPI
  // field description and the CLI `--source-unreconciled` help (final decision 12) -- "in base currency"
  // and "the extraction date" become {base}/{as_at} at their exact points, and addendum E1 has the web
  // hint show the actual conversion date too (T0's own "start of the migrated history"), where Python's
  // text can only say "the conversion date for T0" in the abstract, having no single migration in hand.
  "scope.unreconciled_hint":
    "The key is the bank account's source id (for Xero, the AccountID shown on the Scope screen -- not the ledger code, and not the destination's id). The value is the net amount the source still shows as unreconciled for that account, in {base}, money in positive. It covers every movement dated from the start of the migrated history (the conversion date, {conversion_date}, for T0) up to and including the extraction date ({as_at}) that the source still shows as unreconciled when extraction runs -- items dated before that start are already inside the opening balance and are excluded. Declare only transactions ALREADY ENTERED in the source's books for that account (for Xero: spend money, receive money and payments on the account that are not yet reconciled to a bank statement line). It is not the bank statement balance minus the source's balance: statement lines still waiting in the bank feed are not in the source's books and must not be declared. Example: Xero shows a received payment of 120.00 and a spend of 45.00 on the account that are not reconciled, and 18 statement lines waiting in the feed: declare 75.00. An account left out is exactly 0. Read the figure immediately before scope sign-off, and do not reconcile anything further in the source until extraction has finished.",
  "scope.unreconciled_not_started":
    "Not available yet — listed once the migration has been started and scoped.",
  "scope.unreconciled_list_unknown":
    "This source cannot list its bank accounts. Any declared figure is shown read-only here; enter it through the API or the CLI.",
  "scope.unreconciled_no_accounts": "This source lists no bank accounts.",
  "scope.unreconciled_col_account": "Account",
  "scope.unreconciled_col_currency": "Currency",
  "scope.unreconciled_col_declared": "Declared unreconciled amount",
  "scope.unreconciled_not_declared": "Not declared — treated as 0",
  "scope.unreconciled_foreign_hint": "{currency} account — enter the {base} equivalent",
  "scope.unreconciled_amount_label_for": "Declared unreconciled amount for {account}",
  "scope.unreconciled_archived": "Archived",
  "scope.unreconciled_invalid": "Enter a plain amount like -150.00 (no thousands separator or exponent).",
  "scope.unreconciled_unknown_title": "Unknown bank accounts",
  "scope.unreconciled_unknown_hint": "Remove these before you can save.",
  "scope.unreconciled_unknown_badge": "Not a bank account of this source",
  "scope.unreconciled_remove": "Remove",
  "scope.unreconciled_remove_for": "Remove the declared figure for {key}",
  "scope.unreconciled_save": "Save unreconciled figures",
  // xmg-autoderive A: the figure is calculated from the source's own books and pre-filled, still editable.
  "scope.unreconciled_calculated":
    "Calculated from Xero: {count} unreconciled item(s), net {amount} {base} — check before signing off.",
  "scope.unreconciled_changed_by_you": "You changed it; the calculated value is no longer used.",
  "scope.unreconciled_not_calculated": "Not calculated: {reason}",

  // Manual Xero Journal Report upload (Scope tab): offered only when the connection lacks the journals scope.
  "scope.journals_title": "Journals from Xero",
  "scope.journals_intro":
    "This Xero connection cannot read Xero's journals, so postings Xero makes by itself (stock cost of sales, depreciation, payroll, currency revaluation) cannot be read automatically. Choose how to handle them.",
  "scope.journals_mode_legend": "How to handle Xero's own postings",
  "scope.journals_mode_automatic": "Automatic",
  "scope.journals_mode_automatic_sub":
    "Leave them out. Any difference from Xero's closing trial balance is shown at review and you acknowledge it.",
  "scope.journals_mode_manual": "Manual upload",
  "scope.journals_mode_manual_sub":
    "Upload Xero's Journal Report. Only the postings not already brought in from the rest of Xero are added.",
  "scope.journals_export_hint": "Xero → Reporting → All reports → Journal Report → Export → Excel",
  "scope.journals_export_columns":
    "Tick all 10 columns: Date, Journal ID, Account Code, Account, Narration, Description, Debit, Credit, Posted Date and Posted By. Apply no filter. Xero leaves Narration and Description unticked by default, so tick those two.",
  "scope.journals_export_range": "From {from} to today ({today}, UTC)",
  "scope.journals_export_why":
    "Journals before the conversion date are already in your opening balances, so we don't need them. We need everything up to today so the closing balances can be checked. A wider range is fine: anything outside it is ignored.",
  "scope.journals_upload_label": "Journal Report (.xlsx)",
  "scope.journals_uploading": "Uploading and checking the report…",
  "scope.journals_upload_failed": "The upload did not finish: {reason}",
  "scope.journals_report_on_file": "Report on file: {filename}",
  "scope.journals_report_checked": "{count} journals read from the report.",
  "scope.journals_outcome":
    "{imported} journals imported, {covered} already covered, {review} needing review.",
  "scope.journals_outcome_pending":
    "The report is compared with the rest of Xero's data when the migration extracts.",
  "scope.journals_locked": "The choice is locked at sign-off.",

  // xmg-xerobudget: the source-API call estimate against the vendor's daily budget (Scope tab).
  "scope.api_budget_title": "Source API budget",
  "scope.api_budget_counts": "about {calls} calls over {days} days",
  "scope.api_budget_counts_one_day": "about {calls} calls, fits in one day",
  "scope.preflight_counts_clear": "nothing blocking or warning",
  "scope.preflight_counts_blocking": "{n} blocking",
  "scope.preflight_counts_warnings": "{n} warnings",
  "scope.profiles_counts_same": "the two systems agree",
  "scope.profiles_counts_differ": "{n} differences",
  "scope.entities_counts": "{n} of {total} included",
  "scope.overrides_counts_none": "none switched on",
  "scope.overrides_counts": "{n} switched on",
  "scope.signoff_summary": "What you are signing",
  "scope.api_budget_estimate":
    "This migration needs about {calls} {vendor} calls (at most; the open-item reports are an upper bound).",
  "scope.api_budget_limit":
    "{vendor} allows {limit} per day for this organisation ({origin}); {remaining} are left today.",
  "scope.api_budget_fits": "It fits in one day.",
  "scope.api_budget_days":
    "It will take about {days} days: each night the migration pauses at the daily limit and resumes by itself.",
  "scope.api_budget_origin.env": "set for this deployment",
  "scope.api_budget_origin.configured": "configured for this deployment",
  "scope.api_budget_origin.header": "reported by the vendor",
  "scope.api_budget_origin.default": "the free-tier default",

  // ---------------------------------------------------------------- fx.* (FX opening-balance spec §3.7)
  // Shared by the Scope screen's `FxOpeningBalances` section and the Dry-run screen's `FxOpeningChecks`.
  "fx.title": "Foreign-currency opening balances",
  "fx.caption": "Enter each account's real balance in its own currency, or accept LuciBook's estimate.",
  "fx.blocking": "{count} foreign-currency opening balance(s) must be resolved before sign-off.",
  "fx.col_account": "Account",
  "fx.col_currency": "Currency",
  "fx.col_xero_balance": "Xero {base} balance on {as_at}",
  "fx.col_balance": "Real balance",
  "fx.col_rate": "Implied rate",
  "fx.col_status": "Status",
  "fx.col_actions": "Actions",
  "fx.rate_display": "1 {base} = {rate} {currency}",
  // xmg-fxprefill: no rate yet (nothing entered) is a dash, never the word "None".
  "fx.rate_absent": "—",
  "fx.not_required_line": "Balance is 0 on {date} — nothing to enter",
  "fx.derived_hint": "Calculated from Xero transactions — check against your bank statement",
  "fx.derived_unavailable": "We could not work this balance out from Xero. {reason}",
  "fx.derived_unavailable_next": "Please enter the balance from your bank statement.",
  // xmg-fx-autofill: a figure we filled in is labelled, says where it came from, and can be edited (the
  // ordinary Save; saving makes it the operator's own and removes the label).
  "fx.autofill_badge": "Filled in by us",
  "fx.autofill_source_xero_transactions": "Source: Xero's transactions on this account.",
  "fx.autofill_source_xero_rate_estimate":
    "Source: an estimate at Xero's exchange rate, not a proven figure.",
  "fx.autofill_source_zero_movements": "Source: nothing has moved on this account, so the balance is nil.",
  "fx.action_edit": "Edit",
  "fx.action_restore_auto": "Use our figure again",
  "fx.action_restore_auto_for": "Use our figure again for {account}",
  "fx.action_edit_for": "Edit the figure we filled in for {account}",
  "fx.action_use_derived": "Use this balance",
  "fx.action_use_derived_for": "Use this balance for {account}",
  // Round-3 LOW fix (review-fx-task2-r2.json, item 6): replaces `fx.amount_label` -- two accounts in the
  // SAME currency (e.g. an EUR current and an EUR savings account) got identical accessible names.
  "fx.amount_label_for": "Real balance in {currency} for {account}",
  "fx.status_entered": "Entered",
  "fx.status_estimate_accepted": "Estimate accepted",
  "fx.status_missing": "Missing",
  "fx.status_not_required": "Not required",
  "fx.action_save": "Save",
  "fx.action_clear": "Clear",
  // Round-2 LOW fix (opus review round 5): was hard-coded "GBP only" -- the org's actual base currency
  // may not be GBP, so this now takes `data.base_currency` as `{base}`.
  "fx.action_accept_estimate": "{base} only — accept LuciBook's estimate",
  "fx.action_acknowledge": "Acknowledge",
  // Round-3 LOW fix (review-fx-task2-r2.json, item 6): the Dry-run screen's `FxOpeningChecks` labels its
  // per-finding button by the actual decision now, not a generic "Acknowledge" -- these back the two
  // BLOCK-only options that never had a label of their own outside a dedicated button.
  "fx.action_accept_as_entered": "Accept as entered",
  "fx.action_accept_foreign_balance_lost": "Accept that the foreign balance is lost",
  // Round-2 LOW fix: distinguishable accessible names per account row -- these back an `aria-label`,
  // the VISIBLE button text stays the short label above.
  "fx.action_save_for": "Save the entered balance for {account}",
  "fx.action_clear_for": "Clear the entered balance for {account}",
  "fx.action_accept_estimate_for": "{base} only — accept LuciBook's estimate for {account}",
  "fx.accept_estimate_confirm_title": "Accept LuciBook's estimate for {account}?",
  "fx.accept_estimate_confirm_body":
    "LuciBook will estimate this account's opening balance from the base-currency figure at the ECB rate. The opening balance will not be FX-revalued.",
  // Round-2 MEDIUM-1 (opus review round 5): the generic "Acknowledge" button on the Scope screen's
  // findings list now confirms before firing a CONSEQUENTIAL option (`accept_estimate` or
  // `accept_foreign_balance_lost`) -- a plain WARN `acknowledge` stays one-click.
  "fx.finding_confirm_title_estimate": "Estimate the opening balance for {account}?",
  "fx.finding_confirm_title_lost": "Confirm the foreign balance is lost for {account}?",
  // Round-4 review fix (review-fx-task2-r3.json, item 6): the Scope screen's findings list now confirms
  // an `accept_as_entered` BLOCK too (e.g. the scope-time `fx_opening_balance_sign_mismatch` finding),
  // which needs its own per-account title -- the generic `_entered_generic` below was, until now, only
  // ever reached from the Dry-run screen, which has no per-account object to name.
  "fx.finding_confirm_title_entered": "Accept this figure as entered for {account}?",
  "fx.findings_title": "Open findings",
  // Backlog fix (round-4 follow-up): resolve buttons are correctly disabled once scope is frozen, but the
  // Scope screen gave no hint that an open BLOCK is still resolvable -- on the Dry-run screen.
  "fx.findings_frozen_hint": "Scope is signed off — these findings can no longer be resolved here.",
  "fx.findings_frozen_hint_link": "Resolve them on the Dry-run screen",
  "fx.checks_title": "Foreign-currency opening balance checks",
  "fx.checks_empty": "No foreign-currency opening balance findings.",
  // Round-2 fixes (Dry-run screen's `FxOpeningChecks` -- no per-account `FxAccount` object is available
  // here, only the review item, so the confirmation names the account the same way the message already
  // does, and the accessible name is disambiguated by embedding the message rather than a code/name pair).
  // `fx.action_acknowledge_for_finding` (round-2) is replaced round-3 by `fx.action_for_finding`, which
  // names the actual per-option label rather than hard-coding "Acknowledge".
  "fx.action_for_finding": "{option}: {message}",
  "fx.finding_confirm_title_estimate_generic": "Estimate this account's opening balance?",
  "fx.finding_confirm_title_lost_generic": "Confirm this account's foreign balance is lost?",
  // Round-3 LOW fix (review-fx-task2-r2.json, item 6): the BLOCK-only `accept_as_entered` decision (a
  // rate/sign-mismatch finding) needs its own confirmation title -- it is neither "estimate" nor "lost".
  "fx.finding_confirm_title_entered_generic": "Accept this figure as entered?",
  // xmg-fx-opening-scope: a typed figure whose implied rate is far from Xero's own is flagged on the Scope
  // screen, while it can still be changed. The suggestion only fills the input; Save is still the operator's.
  "fx.rate_warning":
    "This works out at {implied} {currency} per 1 {base}, but Xero's own rate around the opening date was about {reference}. The two are more than 50% apart, so the figure may have been typed wrongly.",
  "fx.rate_use_suggested": "Use {amount} {currency}",
  "fx.rate_use_suggested_for": "Use {amount} {currency} for {account}",
  "fx.rate_suggestion_pending": "Calculated figure filled in — press Save to use it.",
  "fx.action_use_calculated": "Use the calculated figure",
  "fx.action_keep_my_figure": "Keep my figure",
  "fx.finding_confirm_title_calculated": "Replace the figure for {account} with the calculated one?",
  "fx.finding_confirm_title_calculated_generic": "Replace the figure with the calculated one?",

  // ---------------------------------------------------------------- src/screens/review (08)
  "review.title": "Mapping review",
  "review.read_only": "This step is not active any more. It is shown read-only.",
  "review.approve_closed": "This step is closed: there is nothing to approve here any more.",
  "review.auto_approved": "The mapping was approved automatically; there was nothing to review.",
  "review.queues_label": "Review queues",
  "review.mappings_section": "Mappings and queues",
  "review.mappings_accounts": "{n} accounts",
  "review.mappings_tax": "{n} tax rates",
  "review.mappings_contacts": "{n} contacts",
  "review.mappings_dimensions": "{n} dimensions",
  "review.filter_show": "Show",
  "review.chip_blocking": "Blocking",
  "review.chip_warnings": "Warnings",
  "review.tab_accounts": "Chart of accounts",
  "review.tab_tax": "VAT",
  "review.tab_contacts": "Contacts",
  "review.tab_dimensions": "Dimensions",
  "review.filter_legend": "Filter the review queue",
  "review.filter_all": "All",
  "review.filter_unresolved": "Unresolved only",
  "review.empty": "There is nothing to review yet.",
  "review.caption_accounts": "Every account in the chart, with the target it maps to.",
  "review.caption_tax": "Every source VAT rate, with the options the destination offers.",
  "review.caption_contacts": "Every proposed contact merge, with the rule that proposed it.",
  "review.caption_dimensions": "Every source dimension that cannot be carried, with the options.",
  "review.other_items_title": "Other items to acknowledge",
  "review.rest_title": "Other items",
  "review.rest_notes": "{n} for your information",
  "review.rest_open": "{n} still open",
  "review.rest_answered": "{n} answered",
  "review.caption_other":
    "Findings from the source or destination connector that are not a mapping row -- acknowledge each before signing off.",
  "review.col_key": "Item",
  "review.col_severity": "Severity",
  "review.col_message": "What the mapper found",
  "review.details": "Details",
  "review.accept_all": "Accept all suggestions ({n})",
  "review.accept_refused": "{n} suggestions could not be applied and are still open.",
  "review.needs_choice_count": "{n} need your choice (listed first, below)",
  "review.actor_unknown": "an operator",
  "review.actor_rule": "a rule ({rule})",
  "review.rule_acknowledge_only_warning": "the only answer offered, kept as information",
  "review.rule_mapper_suggestion": "the mapper's suggestion",
  "review.rule_fx_convention_from_payments": "exchange rates checked against Xero's own payment amounts",
  "review.rule_reverse_charge_from_supplier_country":
    "the reverse charge that matches where every supplier using it is based",
  "review.rule_journals_gap_report_penny":
    "the uploaded Journal Report left only a one-penny rounding difference that nets to zero",
  "review.rule_unknown": "an automatic check",
  "review.rule_change_hint": "You can still change this answer before approving the mapping.",
  "review.tax_unused_reason":
    "Not used anywhere in this migration, so it is not created in LuciBook. If this code is used again after the conversion date, the hypercare delta will stop until it is mapped.",
  "review.info_title": "For your information",
  "review.caption_info": "Findings that need no answer from you -- nothing here blocks sign-off.",
  "review.suggestions_missing":
    "These items were filed before suggestions existed. Re-run mapping to get suggestions.",
  "review.remap": "Re-run mapping",
  "review.remap_started": "Mapping is re-running. Answers already given on the old mapping are asked again.",
  "review.open_tab": "Open the tab to choose for {code}",
  "review.needs_choice_title": "Needs your choice",
  "review.caption_needs_choice": "Items the mapper cannot decide for you -- pick an option for each.",
  "review.resolve": "Resolve {code}",
  "review.choose_option": "Options for {code}",
  "review.choose_placeholder": "Choose an answer",
  "review.read_options": "Read all {n} options",
  "review.resolve_short": "Resolve",
  "review.resolved_by": "Resolved by {actor} to {target}.",
  "review.badge_resolved": "Resolved",
  "review.option_acknowledge": "Acknowledge",
  "review.option_accept_estimate": "Accept the estimate",
  "review.title_fx_opening_balance_missing": "Foreign-currency opening balance needed",
  "review.title_fx_opening_balance_estimated": "Opening balance will be estimated",
  "review.title_fixed_asset_depreciation_not_aligned":
    "Fixed-asset depreciation runs past the conversion date",
  "review.title_fixed_asset_notes": "Fixed-asset notes",
  "review.title_journals_scope_not_granted": "Xero journals access was not granted",
  "review.title_projects_scope_not_granted": "Xero projects access was not granted",
  "review.title_fx_convention_unverified": "Exchange-rate convention not verified",
  "review.title_items_tracked_not_migrated": "Tracked inventory items are not migrated",
  "review.title_quotes_not_migrated": "Quotes are not migrated",
  "review.title_repeating_invoices_not_migrated": "Repeating invoices are not migrated",
  "review.title_documents_zero_value_lines_dropped": "Zero-value document lines are dropped",
  "review.title_documents_dropped_before_canonical": "Some documents are left out",
  "review.title_depreciation_source_type_unpinned": "Depreciation journal source type not pinned",
  "review.title_fixed_assets_non_gbp": "Fixed assets in a foreign currency",
  "review.title_history_from_not_fy_aligned": "History start is not a financial-year start",
  "review.approve": "Approve and sign off",
  "review.approve_only": "Approve without signing off",
  "review.approve_card_title": "Approve the mapping",
  "review.approve_blocked_one": "1 blocking item is still unresolved.",
  "review.approve_blocked_many": "{count} blocking items are still unresolved.",
  "review.no_blocking_items": "No blocking items",
  "review.open_blocking_one": "open blocking item",
  "review.open_blocking_many": "open blocking items",
  "review.approve_hint":
    "Approves this mapping and moves the migration on to the dry run. Transformation starts straight away.",
  "review.approve_only_hint":
    "Records your approval of this mapping but keeps the migration at this step. Nothing moves on until you sign off.",
  "review.not_ready_title": "Mapping review opens when extraction finishes",
  "review.not_ready_body":
    "The data is still being fetched from the source. There is nothing to review or approve yet; this page updates by itself once the mapping is ready.",
  "review.not_ready_paused":
    "Extraction is paused because the source's daily API limit is spent; it resumes after {at}. Nothing fetched so far is lost.",
  "review.not_ready_running": "Extraction is running.",
  "review.approve_confirm_body":
    "The current mapping version is approved and recorded against your name. Transformation starts immediately.",
  "mapping.col_source_code": "Source code",
  "mapping.col_source_account": "Source account",
  "mapping.not_mapped_yet": "No account chosen yet",
  "mapping.col_maps_to": "Maps to",
  "mapping.col_target_account": "LuciBook account",
  "mapping.coa_counts": "{open} need a LuciBook account, {mapped} mapped",
  "mapping.col_target_code": "Target code",
  "mapping.col_status": "Status",
  "mapping.col_treatment": "Treatment",
  "mapping.col_rate": "Rate",
  "mapping.col_direction": "Direction",
  "mapping.col_vat_only": "VAT only",
  "mapping.col_region": "Region",
  "mapping.col_options": "Options",
  "mapping.badge_system_account": "System account",
  "mapping.badge_renumbered": "Renumbered",
  "mapping.badge_renamed": "Renamed",
  "mapping.badge_greenfield": "New account",
  "mapping.badge_blocked": "Blocked",
  "mapping.renumbered_from": "Renumbered from {from} to {to} — {reason}",
  "mapping.tier_0": "Exact tax or company number",
  "mapping.tier_1": "Name and postcode",
  "mapping.tier_2": "Fuzzy name — a warning, not a merge",
  "mapping.merge_source": "Source contact",
  "mapping.merge_destination": "Destination contact",
  "mapping.decision_label": "Decision",
  "mapping.accept": "Accept",
  "mapping.reject": "Reject",
  "mapping.accepted": "Accepted",
  "mapping.rejected": "Rejected",
  "mapping.undecided": "Undecided",

  // ---------------------------------------------------------------- src/screens/dryrun (08)
  "dryrun.title": "Dry-run report",
  "dryrun.read_only": "This step is not active any more. It is shown read-only.",
  "dryrun.counts_passed": "{n} passed",
  "dryrun.counts_failed": "{n} failed",
  "dryrun.counts_warnings": "{n} warnings",
  "dryrun.counts_warning_one": "1 warning",
  "dryrun.totals_section": "Control totals",
  "dryrun.scorecard_section_counts": "{n} entities, {partial} not fully migrated",
  "dryrun.scorecard_section_counts_all": "{n} entities, all fully migrated",
  "dryrun.signing_section": "What you are signing",
  "dryrun.answers_needed_one": "1 answer needed before you can sign off.",
  "dryrun.answers_needed_many": "{count} answers needed before you can sign off.",
  "dryrun.ready_to_sign": "Nothing is waiting for an answer. You can sign off.",
  "dryrun.run_select": "Comparison run",
  "dryrun.run_latest": "Latest run",
  "dryrun.point": "Comparison point: {point}",
  "dryrun.group": "{check}: {pass} pass / {fail} fail",
  "dryrun.group_warn": "{check}: {pass} pass / {fail} fail / {warn} warn",
  "dryrun.totals_caption": "{point}, {check}: every key compared, failing rows first.",
  "dryrun.col_key": "Key",
  "dryrun.col_source": "Source",
  "dryrun.col_canonical": "Canonical",
  "dryrun.col_destination": "Destination",
  "dryrun.col_tolerance": "Tolerance",
  "dryrun.col_status": "Status",
  "dryrun.empty": "No control totals have been recorded for this run yet.",
  "dryrun.scorecard": "Scorecard",
  "dryrun.col_entity": "Entity",
  "dryrun.col_support": "Support",
  "dryrun.col_source_count": "Source",
  "dryrun.col_canonical_count": "Canonical",
  "dryrun.col_loaded_count": "Loaded",
  "dryrun.col_reason": "Reason",
  "dryrun.not_migrated": "Not migrated, and why",
  "concepts.title": "Features that are not migrated",
  "dryrun.not_migrated_empty": "Every record in scope was migrated.",
  "dryrun.comparison_label": "Comparison points",
  "dryrun.tile_pass": "pass",
  "dryrun.tile_fail": "fail",
  "dryrun.tile_warn": "warn",
  "dryrun.signoff_title": "Sign off the conversion",
  "dryrun.signoff_warn_note": "{count} checks fail. Signing records them against your name.",
  "dryrun.review_again": "Review the mapping again",
  "dryrun.vat_lock_in_dialog": "The VAT-lock notice is shown in the sign-off and acknowledged by signing.",
  "signoff.open": "Sign off the conversion",
  "signoff.open_with_failures": "Sign off with {count} failing checks",
  "signoff.title": "Sign off this conversion",
  "signoff.frozen_scope": "The scope you signed",
  "signoff.history_tier": "History tier: {tier}",
  "signoff.extracted_as_at": "Extracted as at: {date}",
  "signoff.override": "{key} — recorded against {actor}",
  "signoff.not_migrated": "Not migrated, and why",
  "signoff.preflight_warnings": "Warnings carried from profiling",
  "signoff.preflight_empty": "No warnings were carried from profiling.",
  "signoff.hashes": "What is being signed",
  "signoff.package_sha256": "Package hash",
  "signoff.hash_pending": "Not computed yet",
  "signoff.mapping_version": "Mapping version",
  "signoff.scope_sha256": "Scope hash",
  "signoff.failing": "Checks",
  "signoff.failing_suffix": "failing",
  "signoff.failing_check": "{point} / {check} / {key}: {status}",
  "signoff.signer": "Type your name to sign",
  "signoff.signer_hint":
    "Typing your name confirms you have read the list above. The sign-off event records the operator you are signed in as.",
  "signoff.confirm": "I have read the list above and I am signing this conversion.",
  "signoff.submit": "Sign off",
  "signoff.working": "Working…",
  "signoff.in_progress_header": "Signing off…",
  "signoff.in_progress_banner":
    "Your sign-off was accepted and is being processed. This page updates by itself; you do not need to click again.",
  "signoff.package_changed_rerun":
    "The package changed since the dry run (an answer or an update affected it), so we are re-running the dry run. You'll be asked to sign off again in a few minutes.",
  "signoff.package_changed_after_signoff":
    "The package changed after your sign-off, so nothing was loaded and we are re-running the dry run. You'll be asked to sign off again in a few minutes.",
  "signoff.package_rerun_done_after_signoff":
    "The package changed after your sign-off, so nothing was loaded and the dry run was re-run. Check the results below and sign off again.",
  "signoff.package_rerun_done":
    "The dry run was re-run because the package changed after your last review. Check the results below and sign off again.",
  "signoff.still_processing":
    "The sign-off was sent but the migration has not moved on yet: it may still be processing or it may have failed. Refresh the page to see where it has got to before trying again.",
  "signoff.package_changed": "The package changed. Signed hash {expected}, current hash {actual}.",
  "signoff.review_again": "Review the dry run again",
  // xmg-lmux (1): the VAT-lock consequence, shown and acknowledged in the conversion sign-off.
  "signoff.vat_lock": "VAT periods LuciBook will lock",
  "signoff.vat_lock_months":
    "LuciBook will lock these VAT months when the load finishes: {months}. Nothing can be posted into a locked month.",
  "signoff.vat_lock_none_no_hmrc": "No VAT periods will be locked because no HMRC connection is attached.",
  "signoff.vat_lock_none": "No VAT periods will be locked: LuciBook found no filed VAT period it can lock.",
  "signoff.vat_lock_unknown": "LuciBook lists the VAT months it locked when the load finishes.",
  "signoff.vat_lock_already": "Already locked from another source, left as they are: {months}.",
  "signoff.vat_lock_ack": "Signing records your acknowledgement: \u201c{option}\u201d.",
  "signoff.vat_lock_cancel": "Cancel this migration instead",

  // ---------------------------------------------------------------- src/screens/status (09)
  "status.title": "Status",
  "status.live": "The migration is now {state}.",
  "status.hypercare_runs_until": "Runs until {date}",
  "status.days_left": "days left",
  "status.lock_note": "Signing off locks the migration; no further delta is pulled.",
  "status.lock": "Sign off hypercare",
  "status.lock_confirm_title": "Sign off hypercare?",
  "status.lock_confirm_body":
    "The migration is locked. No further delta is pulled and no action remains after this.",
  "status.locked": "This migration is locked. There is nothing further to do.",
  "status.hypercare_blocking":
    "{count} blocking review item(s) must be answered before hypercare can be signed off.",
  "status.hypercare_blocking_link": "Answer them on the Review screen",
  "status.timeline_counts": "{n} events",
  "delta.counts_none": "no changes yet",
  "delta.counts": "{added} added, {changed} changed, {removed} removed",
  "evidence.counts_none": "none yet",
  "evidence.counts": "{n} packs",
  "attachments.counts_skipped": "skipped",
  "status.timeline": "Timeline",
  "status.transition": "{from} \u2192 {to}",
  "status.empty": "No events yet.",
  // hmrc-rerun-action design §3.6/§5.4, critique addendum C2/C3/C9/C10/C12/C13.
  "hmrc_verification.status_label": "HMRC verification:",
  "hmrc_verification.not_run": "HMRC verification runs automatically when extraction starts.",
  "hmrc_verification.verified_at": "Verified against HMRC {at}",
  "hmrc_verification.rerun_at": "Re-run {at}",
  "hmrc_verification.incomplete": "HMRC verification incomplete",
  "hmrc_verification.failed": "HMRC verification failed",
  "hmrc_verification.rerun_button": "Re-run HMRC verification",
  "hmrc_verification.reconnect": "Reconnect HMRC",
  "hmrc_verification.job_failed": "The re-run did not complete; the current HMRC status is shown below.",
  "hmrc_verification.confirm_title": "Re-run HMRC verification?",
  "hmrc_verification.confirm_body_mapping": "The HMRC pass will run again for this migration.",
  "hmrc_verification.confirm_body_conversion_1": "The migration will re-transform and re-run the dry run.",
  "hmrc_verification.confirm_body_conversion_2":
    "HMRC's filed figures may then FAIL the dry run on real differences; cancelling the migration is " +
    "then the exit.",
  "hmrc_verification.confirm_body_conversion_3":
    "If this migration signs off automatically, a clean result goes straight to loading.",
  "status.deltas_title": "Deltas since load",
  "delta.until": "Hypercare runs until {date}.",
  "delta.sweep": "A sweep runs automatically every six hours.",
  "delta.run_now": "Pull deltas now",
  "delta.accepted": "The delta pull was accepted and runs in the background.",
  "delta.col_entity": "Entity",
  "delta.col_added": "Added",
  "delta.col_changed": "Changed",
  "delta.col_removed": "Removed",
  "delta.empty": "No deltas have been pulled yet.",
  "delta.capped": "Xero's daily limit is spent; this delta resumes at {at}.",
  "attachments.title": "Attachments",
  "attachments.summary": "{attached} of {total} files attached; {failed} failed to upload.",
  "attachments.skipped":
    "This destination does not accept attachments; migrated files are kept in the evidence pack.",
  "attachments.phase_failed": "The attachment upload could not continue; see the review items below.",
  "attachments.orphaned":
    "{n} file(s) have no matching record in LuciBook and were kept in the evidence pack only.",
  "attachments.fetch_failed": "{n} file(s) could not be fetched from the source; press Retry to try again.",
  "attachments.last_error": "Last error: {message}",
  "attachments.retry_failed": "Retry failed",
  "attachments.retried": "{n} attachment(s) reset for retry.",
  "attachments.retry_error": "The retry could not be started: {message}",
  "attachments.fetch_retry_blocked":
    "The failed file(s) above could not be retried: the source connection cannot be used any more (it is locked, revoked, or missing its token). This cannot be fixed from the web. If the migration is not locked, an operator can restore the same connection from the command line; if it is locked, these files cannot be fetched again.",
  "evidence.title": "Evidence packs",
  "evidence.col_created": "Created",
  "evidence.col_sha256": "SHA-256",
  "evidence.col_size": "Size (bytes)",
  "evidence.col_retention": "Retained until",
  "evidence.col_store": "Store",
  "evidence.col_status": "Status",
  "evidence.col_download": "Download",
  "evidence.download": "Download pack {id}",
  "evidence.current": "Current",
  "evidence.superseded": "Superseded",
  "evidence.expired": "Expired and deleted",
  "evidence.cli_hint":
    "A pack above the service's download limit cannot be fetched here. Use `lucimig evidence --out` to write it to a file.",
  "evidence.empty": "No evidence packs yet.",

  // ---------------------------------------------------------------- src/screens/auth (P1a §7/§11, P1-web)
  "auth.login.title": "Sign in",
  "auth.login.email": "Email",
  "auth.login.email_placeholder": "you@practice.co.uk",
  "auth.login.password": "Password",
  "auth.login.submit": "Sign in",
  "auth.login.failed": "That email or password was not accepted.",
  "auth.login.request_failed": "Sign-in could not be completed. Reload the page and try again.",
  "auth.login.resend_failed": "The verification email could not be requested. Try again in a moment.",
  "auth.login.throttled": "Too many attempts. Wait {seconds} seconds.",
  "auth.login.unverified": "Verify your email — check your inbox for a verification link.",
  "auth.login.resend": "Resend verification",
  "auth.login.resend_sent": "If that account exists and is not yet verified, a new link is on its way.",
  "auth.login.forgot": "Forgot password?",
  "auth.login.register_prompt": "Need an account?",
  "auth.login.register_link": "Sign up",
  "auth.login.legacy_link": "Sign in with an operator key instead",
  "auth.register.title": "Create an account",
  "auth.register.email": "Email",
  "auth.register.password": "Password",
  "auth.register.password_hint":
    "At least 12 characters. Very common passwords are refused; a few unrelated words make a strong one.",
  "auth.register.display_name": "Display name (optional)",
  "auth.register.submit": "Create account",
  "auth.register.login_prompt": "Already have an account?",
  "auth.register.login_link": "Sign in",
  "auth.register.success_title": "Check your email",
  "auth.register.success_body":
    "We sent a verification link to {email}. Follow it to finish creating your account.",
  "auth.register.by_invitation_title": "Registration is by invitation.",
  "auth.register.by_invitation_body":
    "Only email addresses that have been invited can create an account. If yours has not, ask the person who gave you this link.",
  "auth.register.by_invitation_refused":
    "Registration is by invitation, and this email address has not been invited. Ask the person who gave you this link.",
  "auth.register.resend": "Resend verification",
  "auth.register.consent_prefix": "I agree to the",
  "auth.register.consent_middle": "and the",
  "auth.register.resend_failed": "The verification email could not be requested. Try again in a moment.",
  "auth.register.resend_sent": "If that account exists and is not yet verified, a new link is on its way.",
  "auth.forgot.title": "Reset your password",
  "auth.forgot.email": "Email",
  "auth.forgot.submit": "Send reset link",
  "auth.forgot.success": "If an account exists for that email, we sent a link to reset the password.",
  "auth.forgot.failed": "The reset link could not be requested. Reload the page and try again.",
  "auth.forgot.mail_disabled": "Email delivery is disabled in this deployment — contact an administrator.",
  "auth.forgot.back_to_login": "Back to sign in",
  "auth.reset.title": "Set a new password",
  "auth.reset.password": "New password",
  "auth.reset.confirm": "Confirm new password",
  "auth.reset.submit": "Set new password",
  "auth.reset.mismatch": "Those passwords do not match.",
  "auth.reset.success": "Your password was changed. Sign in with your new password.",
  "auth.reset.invalid": "This reset link is invalid or has expired.",
  "auth.reset.missing_token": "This page was opened without a reset token.",
  "auth.reset.go_to_login": "Go to sign in",
  "auth.reset.request_new": "Request a new reset link",
  "auth.verify.title": "Verifying your email",
  "auth.verify.pending": "Verifying your email…",
  "auth.verify.success": "Your email is verified. You can sign in now.",
  "auth.verify.invalid": "This verification link is invalid or has expired.",
  "auth.verify.failed": "Your email could not be verified right now. Reload the page to try again.",
  "auth.verify.missing_token": "This page was opened without a verification token.",
  "auth.verify.go_to_login": "Go to sign in",
  "auth.login.accepted_flash": "Invitation accepted, please sign in.",
  // The SignIn.html brand panel (handoff §6).
  "auth.brand_headline_1": "Move the books.",
  "auth.brand_headline_2": "Keep every figure accountable.",
  "auth.brand_steps_label": "How a migration runs",
  "auth.brand_step_1": "Connect source and destination",
  "auth.brand_tag_1": "OAuth",
  "auth.brand_step_2": "Scope and sign off",
  "auth.brand_tag_2": "hash frozen",
  "auth.brand_step_3": "Review the mapping",
  "auth.brand_tag_3": "CoA · VAT",
  "auth.brand_step_4": "Dry run against control totals",
  "auth.brand_tag_4": "tie-out",
  "auth.brand_step_5": "Load, verify, hypercare",
  "auth.brand_tag_5": "evidence",
  "auth.brand_vendors": "Xero · QuickBooks Online · FreeAgent · Sage → LuciBook",
  "auth.welcome_back": "Welcome back",
  "auth.or_divider": "or",
  "auth.verify.success_title": "Email verified",
  "auth.link_expired_title": "This link has expired",

  // ---------------------------------------------------------------- P5-web: practice mode
  "practice.landing.title": "Practices",
  "practice.landing.create_first_title": "Create your first practice",
  "practice.landing.create_first_body": "You are not a member of any practice yet.",
  "practice.landing.name_label": "Practice name",
  "practice.landing.create_submit": "Create practice",
  "practice.landing.new_practice": "New practice",
  "practice.landing.cancel_new": "Cancel",
  "practice.landing.member_count": "{count} members",
  "practice.landing.caption": "Practices you are a member of",
  "practice.landing.open": "Open {name}",
  "practice.landing.open_link": "Open →",
  "practice.landing.empty_eyebrow": "Empty state, for comparison",
  "practice.role.OWNER": "Owner",
  "practice.role.STAFF": "Staff",
  "practice.detail.loading_title": "Practice",
  "practice.detail.not_found": "This practice could not be found.",
  "practice.detail.tab_overview": "Overview",
  "practice.detail.tab_clients": "Clients",
  "practice.detail.tab_batch_runs": "Batch runs",
  "practice.detail.tab_members": "Members",
  "practice.detail.tab_gaps": "Authority gaps",
  "practice.detail.tab_budget": "Rate budget",
  "practice.detail.tab_settings": "Settings",
  "practice.detail.tabs_label": "Practice sections",
  "practice.detail.subtitle": "{count} members · you are {role}",
  "practice.overview.review_gaps": "Review gaps →",
  "practice.overview.summary_label": "Practice summary",
  "practice.overview.members": "Members",
  "practice.overview.clients": "Clients",
  "practice.overview.active_batch_runs": "Active batch runs",
  "practice.overview.pending_gaps": "Pending authority gaps",
  "practice.overview.batch_runs_note": "Every batch run this practice has created, from any device.",
  "practice.settings.lucibook_id": "LuciBook practice id",
  "practice.settings.created_at": "Created",
  "practice.settings.synced_at": "Last synced",
  "practice.settings.never_synced": "Never synced",
  "practice.members.caption": "Members of this practice",
  "practice.members.col_member": "Member",
  "practice.members.col_email": "Email",
  "practice.members.col_role": "Role",
  "practice.members.col_accepted": "Accepted",
  "practice.members.col_actions": "Actions",
  "practice.members.empty": "No members yet.",
  "practice.members.make_owner": "Make owner",
  "practice.members.make_staff": "Make staff",
  "practice.members.remove": "Remove",
  "practice.members.remove_confirm_title": "Remove member",
  "practice.members.remove_confirm_body": "Remove {email} from this practice?",
  "practice.members.last_owner_error": "This practice must keep at least one owner.",
  "practice.members.invite_title": "Invite a member",
  "practice.members.invite_email": "Email",
  "practice.members.invite_role": "Role",
  "practice.members.invite_submit": "Send invitation",
  "practice.members.invite_sent": "Invitation sent to {email}.",
  "practice.members.invitations_title": "Pending invitations",
  "practice.members.invitations_caption": "Invitations awaiting acceptance",
  "practice.members.invitations_empty": "No pending invitations.",
  "practice.members.col_invited_by": "Invited by",
  "practice.members.col_expires": "Expires",
  "practice.members.revoke": "Revoke",
  "practice.clients.caption": "Clients synced from LuciBook",
  "practice.clients.col_name": "Name",
  "practice.clients.col_status": "Status",
  "practice.clients.col_cdd": "CDD status",
  "practice.clients.col_synced": "Last synced",
  "practice.clients.empty": "No clients yet.",
  "practice.clients.refresh": "Refresh from LuciBook",
  "practice.clients.sync_pending": "Live sync not yet available in this environment.",
  "practice.clients.sync_started": "A LuciBook sync has been queued.",
  "practice.clients.selected_count": "{count} selected",
  "practice.clients.select_all": "Select all",
  "practice.clients.create_batch_run": "Create batch run",
  "practice.clients.select_at_least_one": "Select at least one client to create a batch run.",
  "practice.clients.prev_page": "Previous page",
  "practice.clients.next_page": "Next page",
  "practice.batch_runs.caption": "This practice's batch runs",
  "practice.batch_runs.col_id": "Batch run",
  "practice.batch_runs.col_kind": "Kind",
  "practice.batch_runs.col_status": "Status",
  "practice.batch_runs.col_created": "Created",
  "practice.batch_runs.col_next_action": "Next action",
  "practice.batch_runs.empty": "No batch runs yet.",
  "practice.batch_runs.open": "Open batch run {id}",
  "practice.batch_run_detail.title": "Batch run",
  "practice.batch_run_detail.clients_label": "{count} clients",
  "practice.batch_run_detail.legend_hypercare": "Hypercare",
  "practice.batch_run_detail.legend_running": "Running",
  "practice.batch_run_detail.legend_awaiting": "Awaiting sign-off",
  "practice.batch_run_detail.legend_queued": "Queued",
  "practice.batch_run_detail.legend_failed": "Failed",
  "practice.batch_run_detail.not_found": "This batch run could not be found.",
  "practice.batch_run_detail.kind": "Kind",
  "practice.batch_run_detail.created_at": "Created",
  "practice.batch_run_detail.next_action": "Next action",
  "practice.batch_run_detail.counts_title": "Counts",
  "practice.batch_run_detail.counts_caption": "Per-state client counts",
  "practice.batch_run_detail.clients_title": "Clients",
  "practice.batch_run_detail.clients_caption": "Every client in this batch run",
  "practice.batch_run_detail.col_client": "Client",
  "practice.batch_run_detail.col_migration_state": "Migration state",
  "practice.batch_run_detail.col_next_action": "Next action",
  "practice.batch_run_detail.send_approvals": "Send approvals",
  "practice.batch_run_detail.approvals_notice":
    "Approval delivery is pending backend implementation — no email has actually been sent.",
  "practice.batch_run_detail.start": "Start migrations",
  "practice.batch_run_detail.start_notice": "Migrations have been queued to start.",
  "practice.batch_run_detail.cancel": "Cancel batch run",
  "practice.batch_run_detail.cancel_confirm_title": "Cancel batch run",
  "practice.batch_run_detail.cancel_confirm_body":
    "This cancels every migration this batch run has started. Continue?",
  "practice.batch_run_detail.cancelled_notice": "This batch run has been cancelled.",
  "practice.batch_run_detail.col_approval_outcome": "Approval",
  "practice.batch_run_detail.col_control_totals": "Control totals tie",
  "practice.gaps.caption": "Clients missing authority",
  "practice.gaps.outstanding": "{count} outstanding",
  "practice.gaps.col_client": "Client",
  "practice.gaps.col_missing": "Missing",
  "practice.gaps.empty": "No authority gaps outstanding.",
  "practice.budget.note": "Showing configured limits — live consumption is not yet available.",
  "practice.budget.caption": "Per-system rate budget",
  "practice.budget.col_system": "System",
  "practice.budget.col_limit": "Daily limit",
  "practice.budget.col_consumed": "Consumed today",
  "practice.budget.col_queued": "Clients queued",
  "practice.budget.col_projected": "Projected finish",
  "practice.budget.empty": "No rate budget data yet.",
  "practice.accept_invite.title": "Accept invitation",
  "practice.accept_invite.missing_token": "This page was opened without an invitation token.",
  "practice.accept_invite.pending": "Accepting your invitation…",
  "practice.accept_invite.invalid": "This invitation is invalid, expired or has already been used.",
  "practice.accept_invite.need_password": "Choose a password to finish creating your account.",
  "practice.accept_invite.password_label": "Password",
  "practice.accept_invite.password_submit": "Accept invitation",
  "practice.accept_invite.unexpected": "Something went wrong accepting this invitation.",
  "practice.accept_invite.go_to_login": "Go to sign in",
  "activity.title": "Live activity",
  "activity.now": "Now",
  "activity.updated": "updated {when}",
  "activity.stopped_because": "Stopped: {reason}",
  "activity.recent": "Recent steps",
  "activity.recent_count": "Recent steps ({n})",
  "outcome.title": "What happened",
  "outcome.open_status": "Open the status page",
  "outcome.not_yet": "Sign-off opens when the dry run has finished.",
  "outcome.moved_on": "This step is done; the migration is now {state}. Follow it on the status page.",
  "outcome.failed": "The migration stopped: {reason}",
  "outcome.failed_no_reason": "The migration stopped. No reason was recorded for this step.",
  "outcome.stopped": "The migration is {state}.",
  "outcome.errors": "Errors the destination reported",
  "outcome.next_title": "What happens next",
  "outcome.next_retry": "Nothing runs until you act. Retry or cancel the migration from the status page.",
  "outcome.next_retry_from":
    "Nothing runs until you act. A retry restarts it from {state}; retry or cancel it from the status page.",
  "outcome.next_none": "Nothing more happens on this migration.",
  // xmg-sec-mfa (S3a): two-step authentication.
  "mfa.eyebrow": "Two-step authentication",
  "mfa.challenge.title": "Enter your code",
  "mfa.challenge.intro": "Open your authenticator app and enter the 6-digit code for LuciMigration.",
  "mfa.challenge.recovery_intro":
    "Enter one of the recovery codes you saved when you set up two-step authentication.",
  "mfa.challenge.code_label": "6-digit code",
  "mfa.challenge.recovery_label": "Recovery code",
  "mfa.challenge.submit": "Verify",
  "mfa.challenge.invalid":
    "That code was not accepted. Check the time on your device and try the newest code.",
  "mfa.challenge.recovery_invalid": "That recovery code was not accepted. Each code works only once.",
  "mfa.challenge.throttled": "Too many attempts. Wait {seconds} seconds.",
  "mfa.challenge.locked":
    "Two-step authentication is locked after too many wrong codes. Ask your LuciMigration operator to reset it.",
  "mfa.challenge.use_recovery": "Use a recovery code instead",
  "mfa.challenge.use_app": "Use the authenticator app instead",
  "mfa.challenge.lost_both":
    "Lost your authenticator and your recovery codes? Ask your LuciMigration operator to reset two-step authentication.",
  "mfa.setup.title": "Set up two-step authentication",
  "mfa.setup.intro":
    "LuciMigration needs a second step at every sign-in. Set it up once with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password and similar).",
  "mfa.setup.email_intro":
    "First, confirm this is your account: we will email a set-up code to your address. Your password alone is not enough to turn this on.",
  "mfa.setup.email_send": "Email me a set-up code",
  "mfa.setup.email_sent":
    "We sent a set-up code to {address}. It works once, in this browser, for 15 minutes.",
  "mfa.setup.email_code_label": "Set-up code from the email",
  "mfa.setup.email_submit": "Continue",
  "mfa.setup.email_resend": "Send a new code",
  "mfa.setup.email_invalid":
    "That set-up code was not accepted. Codes expire after 15 minutes and stop working after a few wrong tries: send a new one.",
  "mfa.setup.email_throttled": "Too many set-up emails. Try again later.",
  "mfa.setup.step_scan": "Scan this QR code with your authenticator app.",
  "mfa.setup.qr_label": "QR code to add LuciMigration to your authenticator app",
  "mfa.setup.step_manual": "Or enter this key by hand:",
  "mfa.setup.secret_label": "Setup key",
  "mfa.setup.code_label": "Enter the 6-digit code the app now shows",
  "mfa.setup.submit": "Turn on two-step authentication",
  "mfa.setup.invalid": "That code did not match. Check the time on your device and try the newest code.",
  "mfa.setup.finish": "Continue to LuciMigration",
  "mfa.codes.title": "Save your recovery codes",
  "mfa.codes.once":
    "These codes are shown only once. Each one signs you in a single time if you lose your authenticator. Keep them somewhere safe.",
  "mfa.codes.list_label": "Recovery codes",
  "mfa.codes.download": "Download codes",
  "mfa.codes.downloaded": "Downloaded. Keep the file somewhere safe and offline.",
  "mfa.codes.saved": "I have saved these codes",
  "mfa.stepup.title": "Confirm it is you",
  "mfa.stepup.body": "This action needs a fresh code from your authenticator app.",
  "mfa.stepup.confirm": "Confirm",
  "mfa.security.title": "Security",
  "mfa.security.caption": "Two-step authentication for your account.",
  "mfa.security.card_title": "Two-step authentication",
  "mfa.security.on": "On",
  "mfa.security.method": "Method",
  "mfa.security.method_totp": "Authenticator app",
  "mfa.security.this_session": "This session",
  "mfa.security.session_totp": "Signed in with the authenticator app",
  "mfa.security.session_recovery": "Signed in with a recovery code",
  "mfa.security.codes_left": "Recovery codes left",
  "mfa.security.step_up_until": "No code needed for sensitive actions until",
  "mfa.security.codes_low": "You are running low on recovery codes. Make a new set.",
  "mfa.security.regenerate_body":
    "Making a new set of recovery codes stops every old code from working. You will be asked for a code first.",
  "mfa.security.regenerate": "Make new recovery codes",
  "mfa.security.done": "Done",
  "mfa.security.lost_both":
    "To move to a new authenticator app, ask your LuciMigration operator to reset two-step authentication; you will set it up again at your next sign-in.",
  // xmg-sec-opsdocs (P1): the privacy policy / terms of use pages and the footer that links them.
  "legal.privacy_link": "Privacy policy",
  "legal.terms_link": "Terms of use",
  "legal.version": "Version {version}",
  "legal.back": "Back to LuciMigration",
} as const;
